/**
 * <subcollection-pills>
 *
 * Turns the subcollection pill row on a collection page into a single-select
 * storefront filter toggle:
 *  - Clicking a pill applies the matching product filter and refreshes the
 *    product grid in place (via the theme's <facet-filters> component).
 *  - All other pills stay visible, so switching club takes a single click.
 *  - Clicking the active pill again clears the filter and restores the full
 *    collection.
 *
 * Falls back to plain link navigation when <facet-filters> is unavailable,
 * because every pill carries a real href.
 */
if (!customElements.get('subcollection-pills')) {
  class SubcollectionPills extends HTMLElement {
    /** Shopify's in-stock filter, which a pill always drops. */
    static AVAILABILITY_PARAM = 'filter.v.availability';

    connectedCallback() {
      this.list = this.querySelector('.subcoll-pills');
      this.scrollWrap = this.querySelector('[data-subcoll-pills-wrap]');

      // Which filters this row drives. Club sides sit in one, national teams in
      // another, so a row can span both and every pill carries its own.
      this.params = (this.dataset.filterParams || '').split(',').filter(Boolean);

      if (!this.list) return;

      this.clickHandler = this.clickHandler || this.handleClick.bind(this);
      this.syncHandler = this.syncHandler || this.syncFromUrl.bind(this);

      this.addEventListener('click', this.clickHandler);
      document.addEventListener('on:facet-filters:updated', this.syncHandler);
      window.addEventListener('popstate', this.syncHandler);

      this.initScrollbar();
      this.scrollActiveIntoView();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.clickHandler);
      document.removeEventListener('on:facet-filters:updated', this.syncHandler);
      window.removeEventListener('popstate', this.syncHandler);
    }

    /**
     * The theme's facet filters component, when filtering is active on this page.
     * @returns {?Element}
     */
    getFacetFilters() {
      const el = document.querySelector('facet-filters');
      return el && typeof el.applyFilters === 'function' ? el : null;
    }

    /**
     * The filter values a pill applies.
     *
     * A club often has several entries in the club metaobject ("Dortmund" next
     * to "Borussia Dortmund"), so one pill can carry more than one value.
     * @param {Element} pill - Pill element.
     * @returns {string[]}
     */
    static getPillValues(pill) {
      try {
        const values = JSON.parse(pill.dataset.filterValues);
        return Array.isArray(values) ? values : [];
      } catch (error) {
        return [];
      }
    }

    /**
     * Handles clicks on a pill that maps to one or more storefront filter values.
     * @param {object} evt - Event object.
     */
    handleClick(evt) {
      if (evt.defaultPrevented || evt.metaKey || evt.ctrlKey || evt.shiftKey || evt.button > 0) return;

      const pill = evt.target.closest('.subcoll-pill[data-filter-values]');
      if (!pill || this.params.length === 0) return;

      const pillValues = SubcollectionPills.getPillValues(pill);
      const pillParam = pill.dataset.filterParam;
      if (pillValues.length === 0 || !pillParam) return;

      const facetFilters = this.getFacetFilters();
      if (!facetFilters) return;

      evt.preventDefault();

      const wasActive = pill.getAttribute('aria-current') === 'true';
      const searchParams = new URLSearchParams(window.location.search);

      // Clear every filter this row drives, so switching from a club to a
      // country pill does not leave the previous one applied.
      this.params.forEach((param) => searchParams.delete(param));
      searchParams.delete('page');

      // A pill shows everything that club has, sold out included. Nearly the
      // whole range is second hand, so a club whose stock is gone would come
      // back "Geen producten gevonden" while its shirts are still on the site.
      // <client-sort> keeps the buyable ones on top.
      searchParams.delete(SubcollectionPills.AVAILABILITY_PARAM);

      if (!wasActive) pillValues.forEach((value) => searchParams.append(pillParam, value));

      // Update the pills straight away so the UI responds without waiting on the fetch.
      this.setActiveValues(wasActive ? [] : pillValues, searchParams);

      facetFilters.applyFilters(searchParams.toString(), evt);
    }

    /**
     * Re-reads the active filter values from the current URL.
     */
    syncFromUrl() {
      if (this.params.length === 0) return;

      const searchParams = new URLSearchParams(window.location.search);
      const values = this.params.flatMap((param) => searchParams.getAll(param));
      this.setActiveValues(values);
    }

    /**
     * Marks the matching pills as active and rewrites every pill href to match.
     * @param {string[]} activeValues - Currently applied filter values.
     * @param {URLSearchParams} [currentParams] - Parameters being applied, when
     *   they are not (yet) reflected in the URL.
     */
    setActiveValues(activeValues, currentParams) {
      const values = Array.isArray(activeValues) ? activeValues : [];
      const source = currentParams || new URLSearchParams(window.location.search);
      const baseParams = new URLSearchParams(source.toString());
      this.params.forEach((param) => baseParams.delete(param));
      baseParams.delete('page');
      baseParams.delete(SubcollectionPills.AVAILABILITY_PARAM);

      this.querySelectorAll('.subcoll-pill[data-filter-values]').forEach((pill) => {
        const pillValues = SubcollectionPills.getPillValues(pill);
        const pillParam = pill.dataset.filterParam;

        // A pill counts as active once any of its values is applied, so a club
        // stays highlighted even when only one of its spellings is in the url.
        const isActive = pillValues.length > 0 && pillValues.some((value) => values.includes(value));

        if (isActive) {
          pill.setAttribute('aria-current', 'true');
        } else {
          pill.removeAttribute('aria-current');
        }

        const pillParams = new URLSearchParams(baseParams.toString());
        if (!isActive && pillParam) pillValues.forEach((value) => pillParams.append(pillParam, value));

        const query = pillParams.toString();
        pill.setAttribute('href', query ? `${window.location.pathname}?${query}` : window.location.pathname);
      });

      this.scrollActiveIntoView();
    }

    /**
     * Horizontally centres the active pill within the scrollable row.
     */
    scrollActiveIntoView() {
      const active = this.querySelector('.subcoll-pill[aria-current="true"]');
      if (!active || !this.list || this.list.scrollWidth <= this.list.clientWidth) return;

      const listRect = this.list.getBoundingClientRect();
      const pillRect = active.getBoundingClientRect();
      const delta = pillRect.left - listRect.left - (listRect.width - pillRect.width) / 2;

      if (Math.abs(delta) < 2) return;

      this.list.scrollTo({
        left: this.list.scrollLeft + delta,
        behavior: this.hasScrolledOnce ? 'smooth' : 'auto'
      });

      this.hasScrolledOnce = true;
    }

    /**
     * Applies the theme's custom scrollbar to the pill row.
     */
    initScrollbar() {
      if (!this.scrollWrap || this.overlayScrollbars) return;

      const init = () => {
        if (!window.OverlayScrollbarsGlobal || this.overlayScrollbars) return;

        this.overlayScrollbars = window.OverlayScrollbarsGlobal.OverlayScrollbars(
          {
            target: this.scrollWrap,
            elements: { viewport: this.list }
          },
          {}
        );
      };

      if (window.OverlayScrollbarsGlobal) {
        init();
      } else {
        window.addEventListener('load', init, { once: true });
      }
    }
  }

  customElements.define('subcollection-pills', SubcollectionPills);
}
